import { useEffect, useMemo, useRef, useState } from 'react';
import { Lang, languages } from '../i18n';

interface Props {
  value: Lang;
  onChange: (lang: Lang) => void;
  compact?: boolean;
}

const LanguagePicker = ({ value, onChange, compact }: Props) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const current = useMemo(() => languages.find((lang) => lang.code === value) ?? languages[0], [value]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  return (
    <div className={`lang-picker ${compact ? 'compact' : ''}`} ref={wrapperRef}>
      <button
        className="lang-trigger"
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="lang-flag">{current.flag}</span>
        <span className="lang-label">{current.label}</span>
        <svg className="lang-chevron" viewBox="0 0 20 20" aria-hidden="true">
          <path
            d="M5 7.5l5 5 5-5"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open ? (
        <ul className="lang-menu" role="listbox" aria-label="Language">
          {languages.map((lang) => (
            <li key={lang.code}>
              <button
                className={`lang-option ${lang.code === value ? 'selected' : ''}`}
                type="button"
                role="option"
                aria-selected={lang.code === value}
                onClick={() => {
                  onChange(lang.code);
                  setOpen(false);
                }}
              >
                <span className="lang-flag">{lang.flag}</span>
                <span className="lang-label">{lang.label}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default LanguagePicker;
